// ─────────────────────────────────────────────────────────────
//  ActivationChecklist — First-session 5-item guide on /home
//
//  Visible during the first 14 days OR until all items complete.
//  No manual dismiss. Firing order:
//    - checklist_viewed (once per session)
//    - checklist_item_completed (when state transitions false→true)
//    - checklist_completed (when all items complete for the first time)
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { track, trackOnce } from '../../services/activation';
import {
  CheckCircle, Circle, Lock, CaretRight, SealCheck,
  UserCircle, ChatCircle, CalendarBlank, FileArrowUp, ChartLineUp,
} from '@phosphor-icons/react';

const ICONS = {
  complete_profile: UserCircle,
  ask_question: ChatCircle,
  add_appointment: CalendarBlank,
  upload_report: FileArrowUp,
  view_weekly_recap: ChartLineUp,
};

export function ActivationChecklist() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  // Stage 1-D: collapsible. Auto-collapses once ≥4/5 items are done so the
  // checklist doesn't dominate /home for engaged users. User can expand.
  const [expanded, setExpanded] = useState(true);
  const prevDoneRef = useRef({});   // map<item_id, bool> — previous completion state
  const completedFiredRef = useRef(false);
  const viewedFiredRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/checklist`);
      setState(data);
      // Fire checklist_viewed once per session when visible
      if (data && !data.hidden && !viewedFiredRef.current) {
        viewedFiredRef.current = true;
        track('checklist_viewed', {
          complete_count: data.complete_count,
          total: data.total,
        });
      }
      // Fire checklist_item_completed for new completions
      if (data?.items) {
        const prev = prevDoneRef.current;
        for (const it of data.items) {
          if (it.done && prev[it.id] === false) {
            track('checklist_item_completed', { item_id: it.id });
          }
          prevDoneRef.current[it.id] = it.done;
        }
      }
      // Fire checklist_completed once when all done
      if (data?.all_done && !completedFiredRef.current) {
        completedFiredRef.current = true;
        trackOnce('checklist_completed', { age_days: data.age_days });
      }
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Soft refresh when tab regains focus — catches completions that happened
    // in-app without re-mounting HomePage.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stage 9 polish: auto-collapse on first load if user has taken at least
  // one meaningful action but is still <4/5 done. Keeps progress visible
  // without dominating Home. Only fires once per mount.
  const autoCollapsedRef = useRef(false);
  useEffect(() => {
    if (autoCollapsedRef.current || !state) return;
    const hasOneAction = state.total > 0 && state.complete_count >= 1;
    const nearlyDone = state.total > 0 && state.complete_count >= state.total - 1;
    if (hasOneAction && !nearlyDone) {
      setExpanded(false);
    }
    autoCollapsedRef.current = true;
  }, [state]);

  if (loading || !state || state.hidden) return null;

  // Stage 9 polish: at ≥4/5 the checklist disappears entirely — it has done
  // its job. The collapsed pill state below is reserved for users who have
  // taken at least one action but aren't done yet.
  if (state.total > 0 && state.complete_count >= state.total - 1) {
    return null;
  }

  const pct = state.total ? Math.round((state.complete_count / state.total) * 100) : 0;
  const hasOneAction = state.total > 0 && state.complete_count >= 1;

  const handleClick = (item) => {
    if (item.locked) return;
    navigate(item.cta_route);
  };

  // Stage 9 polish: collapsed pill if user has taken at least one action but
  // is still <4/5 — keeps progress visible without dominating the dashboard.
  if (hasOneAction && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        type="button"
        data-testid="activation-checklist-collapsed"
        className="w-full flex items-center gap-3 px-4 py-2.5 bg-emerald-50/60 border border-emerald-100 rounded-full text-left hover:bg-emerald-50 transition-colors mb-4"
      >
        <SealCheck size={14} weight="duotone" className="text-emerald-500" />
        <span className="text-[12px] text-[#1F3A2E] font-medium">
          {state.complete_count} of {state.total} done
        </span>
        <span className="text-[11px] text-slate-400 ml-auto">expand</span>
      </button>
    );
  }

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 mb-4"
      data-testid="activation-checklist"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SealCheck size={16} className="text-[#235347]" weight="duotone" />
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Get the most out of Kindra
          </span>
        </div>
        <span
          className="text-[10px] font-semibold text-[#235347] bg-[#235347]/10 rounded-full px-2 py-0.5"
          data-testid="checklist-progress-chip"
        >
          {state.complete_count}/{state.total}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-[#235347] rounded-full transition-all"
          style={{ width: `${pct}%` }}
          data-testid="checklist-progress-bar"
        />
      </div>

      <div className="space-y-2">
        {state.items.map((it) => {
          const Icon = ICONS[it.id] || Circle;
          const disabled = it.locked || it.done;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => handleClick(it)}
              disabled={disabled}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 border transition-colors text-left min-h-[56px] ${
                it.done
                  ? 'border-emerald-100 bg-emerald-50/40 cursor-default'
                  : it.locked
                  ? 'border-slate-100 bg-slate-50/60 cursor-default'
                  : 'border-slate-200 bg-white hover:bg-slate-50 cursor-pointer'
              }`}
              data-testid={`checklist-item-${it.id}`}
              data-done={it.done ? 'true' : 'false'}
              data-locked={it.locked ? 'true' : 'false'}
            >
              {/* Status icon */}
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-slate-100 flex items-center justify-center">
                {it.done ? (
                  <CheckCircle size={18} className="text-emerald-600" weight="fill" />
                ) : it.locked ? (
                  <Lock size={15} className="text-slate-400" weight="duotone" />
                ) : (
                  <Icon size={16} className="text-[#235347]" weight="duotone" />
                )}
              </span>

              {/* Text */}
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-medium ${it.done ? 'text-emerald-800 line-through' : it.locked ? 'text-slate-400' : 'text-[#0F172A]'}`}>
                  {it.title}
                </span>
                <span className="block text-xs text-slate-400 mt-0.5 truncate">
                  {it.subtitle}
                </span>
              </span>

              {/* CTA arrow (only for actionable items) */}
              {!it.done && !it.locked && (
                <CaretRight size={16} className="text-slate-300 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      {state.all_done && (
        <p className="text-[11px] text-emerald-700 mt-4 text-center" data-testid="checklist-all-done-msg">
          You've set yourself up brilliantly. Kindra now has enough to truly help.
        </p>
      )}
    </div>
  );
}

export default ActivationChecklist;
